<script setup>
/**
 * Linear.
 *
 * One app, one card: the shell's header names it and `Integration` carries the
 * connect/disconnect decision. Linear has nothing to configure beyond that, so
 * the body is the card and nothing else.
 */
import { ref, computed, onMounted } from 'vue';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Integration from './Integration.vue';

const store = useStore();

const integrationLoaded = ref(false);

const integration = useFunctionGetter('integrations/getIntegration', 'linear');

const uiFlags = useMapGetter('integrations/getUIFlags');

const isLoading = computed(
  () => !integrationLoaded.value || uiFlags.value.isCreatingLinear
);

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }
  return integration.value.action;
});

const initializeLinearIntegration = async () => {
  await store.dispatch('integrations/get', 'linear');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeLinearIntegration();
});
</script>

<template>
  <SettingsPageShell :title="$t('INTEGRATION_SETTINGS.LINEAR.HEADER')">
    <SettingsSection
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="$t('INTEGRATION_SETTINGS.LOADING')"
    >
      <div class="flex items-center gap-3 px-4 py-4">
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <Integration
      v-else
      :integration-id="integration.id"
      :integration-name="integration.name"
      :integration-description="integration.description"
      :integration-enabled="integration.enabled"
      :integration-action="integrationAction"
      :delete-confirmation-text="{
        title: $t('INTEGRATION_SETTINGS.LINEAR.DELETE.TITLE'),
        message: $t('INTEGRATION_SETTINGS.LINEAR.DELETE.MESSAGE'),
      }"
    />
  </SettingsPageShell>
</template>
